<template>
  <div :class="{ 'master-detail': true, 'master-detail--show-detail': !isIndex }">
    <MobileHeader :label="$t('nav.maintenance')">
      <div class="menu">
        <ul class="menu__items">
          <li
            v-if="hasJob"
            class="menu-item"
          >
            <button
              class="menu-item__link menu-item__link--icon"
              type="button"
              @click="openMobileSearch"
            >
              <div class="menu-item__label">
                <UseSvg
                  :aria-label="$t('general.search')"
                  class="icon"
                  name="search"
                />
              </div>
            </button>
          </li>
          <li class="menu-item">
            <button
              v-if="currentShaft?.policy?.create"
              class="menu-item__link menu-item__link--icon"
              type="button"
              @click="newJobModalOpen = true"
            >
              <div class="menu-item__label">
                <UseSvg
                  class="icon"
                  name="plus"
                />
              </div>
            </button>
          </li>
        </ul>
      </div>
    </MobileHeader>

    <div class="master-detail__master">
      <div class="master-detail__page-title typo-title">
        {{ $t('nav.maintenance') }}
      </div>
      <div
        v-if="hasJob"
        :class="{
          'mobile-overlay': true,
          'show-for-medium': !mobileSearchOpen || !isIndex,
          'flex-grow': search,
        }"
      >
        <div
          :class="{
            'form form--flat': true,
            'form--separated': !mobileSearchOpen,
          }"
        >
          <div class="form__row">
            <div class="input input--light input--flat input--search">
              <div
                :class="{
                  input__prefix: true,
                  'input__prefix--hilight': search,
                }"
              >
                <UseSvg
                  class="icon"
                  name="search"
                />
              </div>
              <input
                ref="searchInput"
                v-model="search"
                :aria-label="$t('shafts.maintenance.search_jobs')"
                :placeholder="$t('shafts.maintenance.search_jobs')"
              >
              <button
                v-if="search || mobileSearchOpen"
                class="input__clear"
                type="button"
                @click="clearSearch"
              >
                <UseSvg
                  :aria-label="$t('general.close_search')"
                  :class="{ icon: true, 'icon--small': !mobileSearchOpen }"
                  :name="mobileSearchOpen ? 'close_circle_24' : 'close_circle_16'"
                />
              </button>
            </div>
          </div>
        </div>

        <template v-if="search && searchResults">
          <HeaderBar :modifiers="['narrow', 'darkest', 'transparent-on-medium']">
            <span class="typo-small">{{ $t('general.results') }}</span>
          </HeaderBar>
          <div
            v-if="searchResults.length > 0"
            class="scroll-container scroll-container--padded"
          >
            <JobCard
              v-for="job in searchResults"
              :key="job.id"
              :job="job"
            />
          </div>
          <EmptyMessage
            v-else
            :message="$t('general.no_results')"
          >
            <UseSvg name="magnifying_glass_circle" />
          </EmptyMessage>
        </template>
        <div v-else />
        <!-- HACK: enforce "form--separated" still does the trick -->
      </div>

      <template v-if="!searchResults">
        <HeaderBar
          v-if="hasJob"
          :modifiers="[
            'narrow',
            'darkest',
            'sticky-with-nav',
            'transparent-on-medium',
          ]"
        >
          <div class="menu">
            <ul class="menu__items">
              <li
                v-if="!loading"
                class="menu-item typo-small"
              >
                <div class="menu-item__link menu-item__link--icon">
                  <router-link
                    activeClass="menu-item__link--active menu-item__link--disabled"
                    class="menu-item__link menu-item__link--icon"
                    :to="{
                      name: 'shaftJobs',
                      params: { scope: 'jobs' }
                    }"
                  >
                    <span class="menu-item__label">
                      {{ jobsCount }} {{ $t('shafts.maintenance.scheduled') }}
                    </span>
                  </router-link>
                </div>
              </li>
              <li
                v-if="!loading"
                class="menu-item typo-small"
              >
                <div class="menu-item__link menu-item__link--icon">
                  <router-link
                    activeClass="menu-item__link--active menu-item__link--disabled"
                    class="menu-item__link menu-item__link--icon"
                    :to="{
                      name: 'shaftJobs',
                      params: { scope: 'completed_jobs' },
                    }"
                  >
                    <span class="menu-item__label">
                      {{ completedCount }} {{ $t('shafts.maintenance.completed') }}
                    </span>
                  </router-link>
                </div>
              </li>
            </ul>
          </div>

          <template #right>
            <Select
              v-if="jobs.length > 1"
              v-model="sortBy"
              :items="sortByOptions"
              :placeholder="$t('general.sort_by')"
            />
          </template>
        </HeaderBar>

        <div
          v-if="!loading && jobs.length > 0"
          class="scroll-container scroll-container--padded"
        >
          <JobCard
            v-for="job in jobs"
            :key="job.id"
            :job="job"
          />
        </div>

        <EmptyMessage
          v-if="!loading && jobs.length === 0"
          :message="$t('shafts.maintenance.jobs_empty_message')"
          :modifiers="['grow', 'center']"
        >
          <OctagonExcalmationSvg />
          <template #actions>
            <button
              v-if="currentShaft?.policy?.add_job"
              class="button button--full-width button--inverse"
              type="button"
              @click="newJobModalOpen = true"
            >
              <div class="button__icon">
                <PlusIcon />
              </div>
              <div class="button__label">
                {{ $t('shafts.maintenance.new_job') }}
              </div>
            </button>
          </template>
        </EmptyMessage>
      </template>
      <Loader v-if="loading" />
      <div
        v-if="jobs.length > 0 && currentShaft?.policy?.add_job"
        class="form form--dark show-for-medium"
      >
        <div class="form-row">
          <button
            class="button button--full-width button--inverse"
            type="button"
            @click="newJobModalOpen = true"
          >
            {{ $t('shafts.maintenance.new_job') }}
          </button>
          <NewJobModal
            v-if="newJobModalOpen"
            @dismiss-modal="newJobModalOpen = false"
            @save="onNewJobSaved"
          />
        </div>
      </div>
    </div>

    <div class="master-detail__detail">
      <EmptyMessage
        v-if="!$route.params.jobId"
        :message="$t('shafts.maintenance.job_will_appear_here')"
        :modifiers="['grow', 'center']"
      >
        <OctagonExcalmationSvg />
      </EmptyMessage>
      <router-view
        @delete-job="deleteJob"
        @update-job="updateJob"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import I18n from 'i18n-js';

// Utils
import { useShaft } from '@/functions/use_remote_object';
import onEscapePressed from '@/functions/on_escape_pressed';
import Searcher from '@/functions/searcher';
import useAjax from '@/functions/use_ajax';

// Components
import EmptyMessage from '@/components/generic/empty_message.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import JobCard from '@/components/shafts/maintenance/job_card.vue';
import Loader from '@/components/generic/loader.vue';
import MobileHeader from '@/components/generic/mobile_header.vue';
import NewJobModal from '@/components/shafts/maintenance/new_job_modal.vue';
import Select from '@/components/form/select.vue';
import UseSvg from '@/components/generic/use_svg.vue';

// Assets
import OctagonExcalmationSvg from '@/images/octagon-exclamation.svg';
import PlusIcon from '@/images/plus-16.svg';

const props = defineProps({
  jobId: { type: String, required: false, default: null },
  organisationId: { type: String, required: true },
  scope: { type: String, default: 'jobs' },
  shaftId: { type: String, required: true },
});

const currentShaft = useShaft(props);

const route = useRoute();
const router = useRouter();
const ajax = useAjax();

const jobs = ref([]);
const jobsCount = ref(null);
const completedCount = ref(null);
const loading = ref(false);
const mobileSearchOpen = ref(false);
const search = ref('');
const searchInput = ref(null);
const searchResults = ref(null);
const sortBy = ref(null);
const newJobModalOpen = ref(false);
const hasJob = computed(() => (jobsCount.value > 0 || completedCount.value > 0));
let searcher;
let searchKeys = ['name'];

async function load() {
  loading.value = true;
  await loadJobs();
  loading.value = false;
}
onMounted(load);
watch(() => props.scope, load);

async function loadJobs() {
  const { organisationId, shaftId, scope } = props;
  const url = `/organisations/${organisationId}/shafts/${shaftId}/${scope}`;

  let { ok, data } = await ajax(url);

  if (ok) {
    jobsCount.value = data.jobs_count;
    completedCount.value = data.completed_count;
    jobs.value = data.jobs;
    jobs.value.forEach(job => addJobName(job));
    buildSearcher();
  }
}

function addJobName(job) {
  const baseElementType = computed(() => job.element_type.split('/')[0]);
  const elementType = computed(() => job.element_type.split('/')[1]);
  job.name = I18n.t(`shafts.element_types.${baseElementType.value}.${elementType.value}`);
}

async function openMobileSearch() {
  search.value = '';
  mobileSearchOpen.value = true;

  await nextTick();
  searchInput.value.focus();
}

function clearSearch() {
  search.value = '';
  mobileSearchOpen.value = false;
}

onEscapePressed(() => {
  clearSearch();
});

const sortByOptions = [
  { value: 'newest', label: I18n.t('general.newest') },
  { value: 'oldest', label: I18n.t('general.oldest') },
];

const isIndex = computed(() => {
  return route.name === 'shaftJobs';
});

watch(() => jobs, buildSearcher);

/**
 * Watch and set search
 */
watch(search, () => {
  if (search.value) {
    searchResults.value = searcher.search(search.value);
  } else {
    searchResults.value = null;
  }
});

/**
 * Watches for changes on the sort by dropdown
 */
watch(sortBy, () => {
  if (sortBy.value === 'newest') {
    jobs.value.sort(function (a, b) {
      return new Date(b.date) - new Date(a.date);
    });
  } else {
    jobs.value.sort(function (a, b) {
      return new Date(a.date) - new Date(b.date);
    });
  }
});

function buildSearcher(items = jobs.value) {
  searcher = new Searcher(items, { keys: searchKeys });
}

function updateJob(jobData) {
  jobs.value.find(({ id }) => id === jobData.id);
  jobs.value = jobs.value.map(job => {
    if (job.id === jobData.id) return { ...job, ...jobData };
    return job;
  });
}

function onNewJobSaved(job) {
  addJobName(job);
  jobs.value = [...jobs.value, job];
  newJobModalOpen.value = false;
  router.push({ name: 'shaftJob', params: { jobId: job.id } });
  buildSearcher();
}

function deleteJob() {
  loadJobs();
}
</script>
